import LockOpenIcon from '@mui/icons-material/LockOpen';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonAddDisabledIcon from '@mui/icons-material/PersonAddDisabled';
import SecurityIcon from '@mui/icons-material/Security';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import AddIcon from '@mui/icons-material/Add';
import MinusIcon from '@mui/icons-material/Remove';
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import { styled } from '@mui/material/styles';
import { ConfirmResponse, useConfirmDialog } from 'app/providers/confirm-dialog';
import { CanceledError } from 'axios';
import Guid from 'common/values/guid/guid';

import {
  createMRTColumnHelper,
  MaterialReactTable,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState,
  useMaterialReactTable
} from 'material-react-table';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import SearchUsersAPIRequest from 'users/entities/super-user/api/request-contracts/search-users-api-request';
import SuperUserAPIService, { SuperUserAPIServiceError } from 'users/entities/super-user/api/super-user-api-service';
import AuthEmailAPIService, { AuthResponse } from "users/entities/user/api/auth-email-api-service";
import User from 'users/entities/user/user';
import { useSession } from 'users/session/session-context';

const RowActionsContainer = styled(Box)(
  () => ({
    display: 'flex',
    flexWrap: 'nowrap'
  }));

type UsersAdminProps = {
  onProfileView: (type: string, id?: string) => void;
};

export default function UsersAdmin(props: Readonly<UsersAdminProps>) {
  const { onProfileView } = props;
  const [isLoading, setIsLoading] = React.useState(false);
  const [isRefetching, setIsRefetching] = React.useState(false);
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const [data, setData] = React.useState<User[]>([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [wasErrorLoading, setWasErrorLoading] = React.useState(false);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const confirm = useConfirmDialog();
  const session = useSession();
  const navigate = useNavigate();

  useEffect(
    () => {
      let abortController = new AbortController();
      void fetchData(abortController);
      return () => {
        abortController.abort();
        abortController = new AbortController();
      };
    },
    [
      columnFilters,
      query,
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
    ]
  );

  const fetchData = async (abortController?: AbortController) => {
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }

    try {
      const apiService = new SuperUserAPIService(session)
      const request = new SearchUsersAPIRequest(
        query ?? "",
        pagination.pageIndex,
        pagination.pageSize,
        sorting.length > 0 ? sorting[0] : undefined
      );
      const pagedUserResponse = await apiService.searchUsers(
        request,
        abortController
      );

      setData(pagedUserResponse.deserializeUsers());
      setRowCount(pagedUserResponse.totalCount);
      setPagination({
        pageIndex: pagedUserResponse.pageIndex,
        pageSize: pagedUserResponse.pageSize,
      });
    } catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
      setWasErrorLoading(true);
      return;
    }
    setIsLoading(false);
    setIsRefetching(false);
    setWasErrorLoading(false);
  };

  async function registerSuperUser(_event: React.MouseEvent<HTMLButtonElement, MouseEvent>, user: User) {
    if (!user) return;
    const response = await confirm({
      title: `Register ${user.email.value} as a superuser?`,
      message: "Are you sure you want to register this user as a superuser?",
      okButtonText: "Yes",
      cancelButtonText: "No"
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const superUserService = new SuperUserAPIService(session);
      await superUserService.register(user);
      await fetchData();
    } catch (err) {
      if (err instanceof SuperUserAPIServiceError) {
        console.error("Couldn't register superuser");
        enqueueSnackbar(
          "Couldn't register superuser",
          { variant: 'error' }
        );
      } else throw err;
    }
  }

  async function unRegisterSuperUser(
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    user: User
  ) {
    const response = await confirm({
      title: `Unregister ${user.email.value} as a superuser?`,
      message: "Are you sure you want to unregister this user as a superuser?",
      okButtonText: "Yes",
      cancelButtonText: "No"
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const superUserService = new SuperUserAPIService(session);
      await superUserService.unregister(user);
      await fetchData();
    } catch (err) {
      if (err instanceof SuperUserAPIServiceError) {
        console.error("Couldn't unregister superuser");
        enqueueSnackbar(
          "Couldn't unregister superuser",
          { variant: 'error' }
        );
      } else throw err;
    }
  }

  async function disableUser(
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    user: User
  ) {
    const response = await confirm({
      title: `Disable ${user.email.value}?`,
      message: "Are you sure you want to disable this user?",
      okButtonText: "Yes",
      cancelButtonText: "No"
    })

    if (response === ConfirmResponse.Cancel) return;

    try {
      const superUserService = new SuperUserAPIService(session);
      await superUserService.disableUser(user);
      enqueueSnackbar(
        "Disabled user.",
        { variant: 'success' }
      );
      await fetchData();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Couldn't disable user",
        { variant: 'error' }
      );
    }
  }

  async function enableUser(
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    user: User
  ) {
    const response = await confirm({
      title: `Enable ${user.email}?`,
      message: "Are you sure you want to enable this user?",
      okButtonText: "Yes",
      cancelButtonText: "No"
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const superUserService = new SuperUserAPIService(session);
      await superUserService.enableUser(user);
      enqueueSnackbar(
        "Re-enabled user.",
        { variant: 'success' }
      );
      await fetchData();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Couldn't re-enable user",
        { variant: 'error' }
      );
    }
  }

  async function resetUserPassword(
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    data: User | User[]
  ) {
    let user: User;
    if (Array.isArray(data)) {
      user = data[0];
    } else {
      user = data;
    }
    const response = await confirm({
      title: `Reset ${user.email}'s password?`,
      message: "Are you sure you want to reset this user's password?",
      okButtonText: "Yes",
      cancelButtonText: "No"
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await AuthEmailAPIService.requestPasswordReset(user.email);
      enqueueSnackbar(
        "Sent password reset email to user",
        { variant: 'success' }
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Couldn't reset user password",
        { variant: 'error' }
      );
    }
  }

  async function loginAsUser(
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    data: User | User[]
  ) {
    let user: User;
    if (Array.isArray(data)) {
      user = data[0];
    } else {
      user = data;
    }
    const response = await confirm({
      title: `Login as ${user.email}?`,
      message: "Are you sure you want to login as this user?",
      okButtonText: "Yes",
      cancelButtonText: "No"
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const superUserService = new SuperUserAPIService(session);
      setIsLoading(true);
      const authResponse: AuthResponse = await superUserService.loginAsUser(user);
      session.initializeFromAuthResponse(authResponse);
      navigate(
        '/dashboard',
        { replace: true }
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Couldn't login as user",
        { variant: 'error' }
      );
    } finally {
      setIsLoading(false);
    }
  }

  const renderCompanyColumn = (user: User) => {
    if (!user.companyId) return null;
    return (
      <Link
        style={{
          color: '#000',
          cursor: 'pointer',
          textDecoration: 'underline'
        }}
        onClick={() => {
          onProfileView(
            'company',
            user?.companyId?.toString()
          );
        }}
      >

        {user.companyName}
      </Link>
    );
  }

  function renderEmailCell(email: string) {
    return (
      <Typography
        variant="body2"
        sx={{
          fontWeight: "500",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {email}
      </Typography>
    );
  }

  function renderClientVendorCell(isChecked: boolean | undefined) {
    return (
      <Checkbox
        checked={isChecked ?? false}
        indeterminate={isChecked === undefined}
        disabled
        size={'small'}
        sx={{
          padding: 0,
          '&.Mui-checked': {
            color: 'orange',
          },
        }}
      />
    );
  }

  const columnHelper = createMRTColumnHelper<User>();
  const columns = [
    columnHelper.accessor(
      (user) => user.email.value,
      {
        header: "Email",
        id: "email",
        Cell: ({ row }) => renderEmailCell(row.original.email.value)
      }
    ),
    columnHelper.accessor(
      (user) => user.name.firstName,
      {
        header: "First Name",
        id: "firstName",
      }
    ),
    columnHelper.accessor(
      (user) => user.name.lastName,
      {
        header: "Last Name",
        id: "lastName",
      }
    ),
    columnHelper.accessor(
      renderCompanyColumn,
      {
        header: "Company",
        id: "companyName",
      }
    ),
    columnHelper.accessor(
      (user) => user.isVendor,
      {
        header: "Vendor?",
        id: "isVendor",
        Cell: ({ cell }) => renderClientVendorCell(cell.getValue())
      }
    ),
    columnHelper.accessor(
      (user) => user.isClient,
      {
        header: "Client?",
        id: "isClient",
        Cell: ({ cell }) => renderClientVendorCell(cell.getValue())
      }
    ),
  ];

  const table = useMaterialReactTable({
    columns,
    data,
    enableRowSelection: true,
    enableTableHead: true,
    enableRowActions: true,
    enableColumnPinning: true,
    enableGlobalFilter: true,
    initialState: {
      columnPinning: { right: ['mrt-row-actions'] },
      showGlobalFilter: true
    },
    muiExpandButtonProps: ({ row }) => ({
      children: row.getIsExpanded() ? <MinusIcon /> : <AddIcon />,
    }),
    renderRowActions: ({ row, table: _ }) => (
      <RowActionsContainer>
        <Tooltip title={row.original.isSuperUser ? 'Unregister As Superuser' : 'Register As Superuser'}>
          <IconButton
            color={row.original.isSuperUser ? 'error' : 'primary'}
            onClick={(event) => {
              if (row.original.isSuperUser) {
                void unRegisterSuperUser(
                  event,
                  row.original
                )
              } else {
                void registerSuperUser(
                  event,
                  row.original
                )
              }
            }}
          >
            <SecurityIcon style={{ color: row.original.isSuperUser ? 'error' : 'primary' }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Force Password Reset">
          <IconButton
            color="primary"
            onClick={(event) => resetUserPassword(
              event,
              row.original
            )}
          >
            <VpnKeyIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Login As User">
          <IconButton
            color="secondary"
            onClick={(event) => {
              void loginAsUser(
                event,
                row.original
              );
            }}
          >
            <LockOpenIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={row.original.userIsDisabled ? 'Enable User' : 'Disable User'}>
          <IconButton
            color="error"
            onClick={(event) => {
              if (row.original.userIsDisabled) {
                void enableUser(
                  event,
                  row.original
                )
              } else {
                void disableUser(
                  event,
                  row.original
                )
              }
            }}
          >
            {row.original.userIsDisabled ? <PersonAddIcon color='primary' /> : <PersonAddDisabledIcon color='error' />}
          </IconButton>
        </Tooltip>
      </RowActionsContainer>

    ),
    getRowId: (row) => row.id?.toString() ?? Guid.generate().toString(),
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    manualPagination: true,
    manualSorting: true,
    rowCount: rowCount,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setQuery,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: wasErrorLoading
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    state: {
      columnFilters,
      globalFilter: query,
      isLoading,
      pagination,
      showAlertBanner: wasErrorLoading,
      showProgressBars: isRefetching,
      sorting,
    },
  });

  return (
    <MaterialReactTable table={table} />
  );
}
